import { useEffect, useRef, useState } from 'react'
import { type UIMatch, useMatches, useNavigation } from 'react-router'

const ANNOUNCEMENT_DELAY = 350
const CLEAR_DELAY = 3000

type CustomMatch = UIMatch<unknown, RouteHandle | undefined>

type RouteHandle = {
	title?: string
}

const LiveAnnouncement = (properties: React.ComponentPropsWithoutRef<'div'>) => {
	const matches = useMatches() as CustomMatch[]
	const routeTitle = matches.findLast((match) => match.handle?.title)?.handle?.title
	const { state: navigationState } = useNavigation()

	const announcementRef = useRef<HTMLDivElement>(null)
	const previousTitleRef = useRef<null | string>(null)
	const announceTimerRef = useRef<null | ReturnType<typeof setTimeout>>(null)
	const clearTimerRef = useRef<null | ReturnType<typeof setTimeout>>(null)
	const mountedRef = useRef(true)

	const [titleKey, setTitleKey] = useState(0)

	useEffect(() => {
		mountedRef.current = true
		return () => {
			mountedRef.current = false
		}
	}, [])

	useEffect(() => {
		const titleElement = document.querySelector('title')
		if (!titleElement) return
		const observer = new MutationObserver(() => {
			setTitleKey((previous) => previous + 1)
		})
		observer.observe(titleElement, { characterData: true, childList: true, subtree: true })
		return () => {
			observer.disconnect()
		}
	}, [])

	useEffect(() => {
		const clearTimers = () => {
			if (announceTimerRef.current !== null) {
				clearTimeout(announceTimerRef.current)
				announceTimerRef.current = null
			}
			if (clearTimerRef.current === null) {
				return
			}

			clearTimeout(clearTimerRef.current)
			clearTimerRef.current = null
		}

		const announce = (text: string) => {
			if (mountedRef.current && announcementRef.current) {
				announcementRef.current.textContent = text
			}
		}

		if (navigationState === 'loading') {
			clearTimers()
			announce('Завантаження...')
			previousTitleRef.current = null
			return clearTimers
		}

		if (navigationState !== 'idle') return

		const h1TextContent = document.querySelector('h1')?.textContent
		const title =
			((routeTitle ?? document.title.split(/[|·-]/, 1)[0]?.trim()) || (h1TextContent == null ? undefined : h1TextContent.trim())) ??
			'Сторінка оновлена'

		if (title === previousTitleRef.current) return

		clearTimers()
		previousTitleRef.current = title

		announceTimerRef.current = setTimeout(() => {
			announce(`Навігація: ${title}`)
			announceTimerRef.current = null

			clearTimerRef.current = setTimeout(() => {
				announce('')
				clearTimerRef.current = null
			}, CLEAR_DELAY)
		}, ANNOUNCEMENT_DELAY)

		return clearTimers
	}, [routeTitle, titleKey, navigationState])

	return (
		<div {...properties} aria-atomic="true" aria-live="polite" className="sr-only" id="live-announcement" ref={announcementRef} role="status" />
	)
}

export default LiveAnnouncement
